/* DealCoordinationPanel.jsx — Phase 38 Deal Room coordination panel.
 *
 * Calm per-deal coordination plan based on the deal's current
 * pending automation runs + historical recovery routes. Reads from
 * `GET /api/coordination/deals/{deal_id}`.
 *
 * Auto-hides when there is no pending coordination on the deal.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Workflow, Timer } from "lucide-react";
import { api } from "../../lib/api";

const POLL_MS = 120_000;

const ROUTE_TONE = {
  "Primary route":         { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  "Reliable route":        { bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  "Variable route":        { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  "Route under review":    { bg: "rgba(244,63,94,0.10)",  fg: "#9F1239" },
};

const cardStyle = {
  borderRadius: "16px",
  border: "1px solid var(--gp-card-border, #E2E8F0)",
  background: "var(--gp-card-bg, #FFFFFF)",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

export function DealCoordinationPanel({ dealId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await api.get(`/api/coordination/deals/${dealId}`);
      setData(res.data?.deal_coordination || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => {
    const id = setInterval(refetch, POLL_MS);
    return () => clearInterval(id);
  }, [refetch]);

  if (loading || !data || !data.active) return null;

  return (
    <section className="p-4" style={cardStyle}
             data-testid="deal-coordination-panel">
      <div className="flex items-center gap-1.5 mb-1">
        <Workflow size={11} strokeWidth={2.3} className="text-violet-600" />
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700">
          Coordination plan
        </p>
      </div>
      <h3 className="text-[13px] font-semibold text-slate-900 leading-tight"
          data-testid="deal-coordination-narrative">
        {data.narrative}
      </h3>
      <ul className="mt-2.5" data-testid="deal-coordination-list">
        {(data.plan || []).map((step, i) => {
          const route = step.historical_route;
          const tone = ROUTE_TONE[route?.band] || ROUTE_TONE["Reliable route"];
          const slug = (step.run_id || `r-${i}`).replace(/[^A-Za-z0-9_-]+/g, "-");
          return (
            <li key={slug}
                className="py-2 border-t first:border-t-0"
                style={{ borderColor: "var(--gp-card-border)" }}
                data-testid={`deal-coordination-step-${slug}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[12px] font-semibold text-slate-900 truncate min-w-0">
                  {step.trigger_kind.replace(/_/g, " ")}
                  <span className="ml-1 text-[10px] font-normal text-slate-500">
                    → {step.action_kind.replace(/_/g, " ")}
                  </span>
                </p>
                <div className="flex items-center gap-1.5 shrink-0">
                  {step.latency_flag ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-md"
                          style={{ background: "rgba(244,63,94,0.08)", color: "#9F1239" }}
                          data-testid={`deal-coordination-latency-${slug}`}>
                      <Timer size={9} strokeWidth={2.5} />
                      latency
                    </span>
                  ) : null}
                  {route ? (
                    <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
                          style={{ background: tone.bg, color: tone.fg }}
                          data-testid={`deal-coordination-band-${slug}`}>
                      {route.band}
                    </span>
                  ) : null}
                </div>
              </div>
              <p className="text-[10.5px] text-slate-500 mt-0.5">
                primary: {step.primary_role.replace(/_/g, " ")}
                {step.age_hours != null ? ` · age ${step.age_hours}h` : ""}
                {route?.success_rate_pct != null
                  ? ` · historical ${route.success_rate_pct}%` : ""}
                {route?.median_resolution_hours != null
                  ? ` · median ${route.median_resolution_hours}h` : ""}
              </p>
              {step.recovery_path?.step_2 ? (
                <p className="text-[10.5px] text-slate-500 mt-0.5">
                  if needed → escalate to {step.recovery_path.step_2.primary_role.replace(/_/g, " ")}
                  {` · median ${step.recovery_path.step_2.median_window_hours}h`}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default DealCoordinationPanel;
